import axios from "axios";

import { concat, pick } from "ramda";
import { MATCHING_GIFTS_ENDPOINT, MATCHING_GIFTS_API } from "../api";
import {
  defaultSort,
  pluckData,
  sortCardData,
} from "shared/components/MatchingGifts/utils/utils";

// Redux Prefix
const prefix = concat("MATCHING_GIFTS:");

// Actions
const FETCHING_FROM_API = prefix("FETCHING_FROM_API");
const FETCH_SUCCESS = prefix("FETCH_SUCCESS");
const FETCH_ERROR = prefix("FETCH_ERROR");

const SUBMIT_MATCHING_GIFT_TO_API = prefix("SUBMIT_MATCHING_GIFT_TO_API");
const SUBMIT_MATCHING_GIFT_SUCCESS = prefix("SUBMIT_MATCHING_GIFT_SUCCESS");
const SUBMIT_MATCHING_GIFT_ERROR = prefix("SUBMIT_MATCHING_GIFT_ERROR");

const UPDATE_SEARCH = prefix("UPDATE_SEARCH");
const UPDATE_SORT = prefix("UPDATE_SORT");
const OPEN_MODAL = prefix("OPEN_MODAL");
const CLOSE_MODAL = prefix("CLOSE_MODAL");
const PROCEED_GIFT_CREATE_PAYMENT = prefix("PROCEED_GIFT_CREATE_PAYMENT");
const PROCEED_GIFT_CREATE_CONFIRMATION = prefix("PROCEED_GIFT_CREATE_CONFIRMATION");
const PROCEED_DONATE_GIFT_PAYMENT = prefix("PROCEED_DONATE_GIFT_PAYMENT");
const PROCEED_DONATE_GIFT_CONFIRMATION = prefix("PROCEED_DONATE_GIFT_CONFIRMATION");
const PROCEED_THANKYOU = prefix("PROCEED_THANKYOU");

const SET_CUSTOM_FIELDS = prefix("SET_CUSTOM_FIELDS");
const SET_SPREEDLY_KEY = prefix("SET_SPREEDLY_KEY");
const SET_SPREEDLY_TOKEN = prefix("SET_SPREEDLY_TOKEN");
const SET_GIFT_CREATE_REGISTRATION_VALUES = prefix("SET_GIFT_CREATE_REGISTRATION_VALUES");
const SET_GIFT_CREATE_IMAGE_PREVIEW = prefix("SET_GIFT_CREATE_IMAGE_PREVIEW");
const SET_SPREEDLY_ERRORS = prefix("SET_SPREEDLY_ERRORS");
const SET_GIFT_CREATE_PAYMENT = prefix("SET_GIFT_CREATE_PAYMENT");
const SET_SECTION_ID = prefix("SET_SECTION_ID");

const SET_LOADING_DONE = prefix("SET_LOADING_DONE");
const SET_LOADING = prefix("SET_LOADING");

const RESET_GIFT_CREATE_REGISTRATION = prefix("RESET_GIFT_CREATE_REGISTRATION");

const initialFormValues = [
  "currentFormObjectRegistration",
  "currentFormObjectPayment",
  "donationError",
  "spreedlyToken",
  "spreedlyKey",
];

const INITIAL_STATE = {
  data: {},
  page: 1,
  totalCount: 0,
  totalPages: 0,
  sectionId: "",
  donationError: null,
  loading: false,
  cardData: [],
  originalCardData: [],
  campaignData: [],
  customFields: [],
  sort: defaultSort,
  searchTerm: "",
  selectedModal: "",
  modalOpen: false,
  spreedlyKey: "",
  spreedlyToken: "",
  spreedlyErrors: "",
  currentStep: "",
  currentFormObjectRegistration: {},
  imagePreview: "",
  currentFormObjectPayment: {},
  campaignCustomFields: {},
  currentMatchingGift: {
    id: "",
    title: "",
    image_filename: "",
    campaign_name: "",
    full_description: "",
    campaign: {},
    money_goal: "",
    complete: "",
    expired: "",
    end_date: "",
    time_remaining: 0,
    active: "",
    type: "",
  },
};

// Action Creators

export const setLoading = () => ({
  type: SET_LOADING,
});

export const setLoadingDone = () => ({
  type: SET_LOADING_DONE,
});

export const fetchDataFromApi = (id) => ({
  type: FETCHING_FROM_API,
  payload: { id },
});

export const fetchSuccess = (data) => ({
  type: FETCH_SUCCESS,
  payload: { data },
});

export const fetchError = (error) => ({
  type: FETCH_ERROR,
  payload: { error },
});

export const submittingMatchingGift = () => ({
  type: SUBMIT_MATCHING_GIFT_TO_API,
});

export const submitMatchingGiftSuccess = ({ data: { matching_gift } }) => ({
  type: SUBMIT_MATCHING_GIFT_SUCCESS,
  payload: { matching_gift },
});

export const submitMatchingGiftError = (error) => ({
  type: SUBMIT_MATCHING_GIFT_ERROR,
  payload: { error },
});

export const proceedToCreateGiftPaymentStep = () => ({
  type: PROCEED_GIFT_CREATE_PAYMENT,
});

export const proceedToCreateGiftConfirmationStep = () => ({
  type: PROCEED_GIFT_CREATE_CONFIRMATION,
});

export const proceedToThankyouStep = () => ({
  type: PROCEED_THANKYOU,
});

export const setGiftCreateRegistrationValues = (values) => ({
  type: SET_GIFT_CREATE_REGISTRATION_VALUES,
  payload: {
    values,
  },
});

export const setGiftCreateImagePreview = (preview) => ({
  type: SET_GIFT_CREATE_IMAGE_PREVIEW,
  payload: preview,
});

export const setGiftCreatePaymentValues = (values) => ({
  type: SET_GIFT_CREATE_PAYMENT,
  payload: {
    values,
  },
});

export const openModal = (selectedModal, currentMatchingGift, currentStep) => ({
  type: OPEN_MODAL,
  payload: {
    currentStep,
    selectedModal,
    currentMatchingGift,
  },
});

export const closeModal = () => ({
  type: CLOSE_MODAL,
});

export const updateSearch = (event) => ({
  type: UPDATE_SEARCH,
  payload: { searchTerm: event.target.value },
});

export const updateSortOptions = (event) => ({
  type: UPDATE_SORT,
  payload: { sort: event.target.value },
});

export const setCustomFieldsAction = (campaignCustomFields) => ({
  type: SET_CUSTOM_FIELDS,
  payload: {
    campaignCustomFields,
  },
});

export const setSpreedlyErrorsAction = (errors) => ({
  type: SET_SPREEDLY_ERRORS,
  payload: {
    errors,
  },
});

export const setSpreedlyKeyAction = (spreedlyKey) => ({
  type: SET_SPREEDLY_KEY,
  payload: {
    spreedlyKey,
  },
});

export const setSpreedlyTokenAction = (spreedlyToken) => ({
  type: SET_SPREEDLY_TOKEN,
  payload: {
    spreedlyToken,
  },
});

export const setSectionId = (sectionId) => ({
  type: SET_SECTION_ID,
  payload: {
    sectionId,
  },
});

export const fetchFromApi = (id, entityId, page, sort, searchTerm) => {
  return async (dispatch) => {
    dispatch(fetchDataFromApi(id));

    axios
      .get(
        `${MATCHING_GIFTS_ENDPOINT}/${id}?entity_id=${entityId}&page=${page}&sort=${sort}&search=${searchTerm}`,
      )
      .then((response) => {
        dispatch(fetchSuccess({ ...pluckData(response), ...{ sort, searchTerm } }));
      })
      .catch((error) => dispatch(fetchError(error)));
  };
};

export const submitMatchingGift = (formData) => {
  return async (dispatch) => {
    const config = {
      headers: { "content-type": "multipart/form-data" },
    };
    dispatch(submittingMatchingGift());
    dispatch(setLoading());
    axios
      .post(MATCHING_GIFTS_API, formData, config)
      .then((response) => {
        dispatch(submitMatchingGiftSuccess(response));
      })
      .catch((error) => {
        dispatch(submitMatchingGiftError(error));
      });
  };
};

export const resetGiftCreateRegistration = () => ({
  type: RESET_GIFT_CREATE_REGISTRATION,
});

// Reducer
export default function reducer(state = INITIAL_STATE, action) {
  const { payload } = action;
  const { originalCardData } = state;

  switch (action.type) {
    case SET_LOADING:
      return {
        ...state,
        loading: true,
      };
    case SET_LOADING_DONE:
      return {
        ...state,
        loading: false,
      };
    case FETCHING_FROM_API:
      return { ...INITIAL_STATE, loading: true };
    case FETCH_SUCCESS:
      return {
        ...state,
        data: payload.data.matching_gift,
        sectionId: payload.data.matching_gift.id,
        page: payload.data.matching_gift.page,
        totalCount: payload.data.matching_gift.total_count,
        totalPages: payload.data.matching_gift.total_pages,
        sort: payload.data.sort,
        searchTerm: payload.data?.searchTerm ?? "",
        cardData:
          payload.data.matching_gift.matching_gifts.map((v, ii) => ({
            ...v,
            index: ii,
          })) || [],
        originalCardData:
          payload.data.matching_gift.matching_gifts.map((v, ii) => ({
            ...v,
            index: ii,
          })) || [],
        campaignData: payload.data.campaign_data || [],
        customFields: payload.data.custom_fields || [],
        loading: false,
      };
    case FETCH_ERROR:
      return { ...state, loading: false, error: action.payload.error };
    case UPDATE_SORT:
      return {
        ...state,
        sort: action.payload.sort,
        cardData: sortCardData({
          originalCardData,
          sort: action.payload.sort,
          search: state.searchTerm,
        }),
      };
    case UPDATE_SEARCH:
      return {
        ...state,
        searchTerm: action.payload.searchTerm,
        cardData: sortCardData({
          originalCardData,
          sort: state.sort,
          search: action.payload.searchTerm,
        }),
      };
    case OPEN_MODAL:
      return {
        ...state,
        currentStep: action.payload.currentStep,
        selectedModal: action.payload.selectedModal,
        currentMatchingGift: action.payload.currentMatchingGift,
        modalOpen: true,
      };
    case CLOSE_MODAL:
      return {
        ...state,
        selectedModal: "",
        modalOpen: false,
        ...pick(initialFormValues, INITIAL_STATE),
      };
    case PROCEED_GIFT_CREATE_PAYMENT:
      return {
        ...state,
        currentStep: "giftCreatePaymentStep",
      };
    case PROCEED_GIFT_CREATE_CONFIRMATION:
      return {
        ...state,
        currentStep: "giftCreateConfirmationStep",
        spreedlyErrors: "",
      };
    case PROCEED_DONATE_GIFT_PAYMENT:
      return {
        ...state,
        currentStep: "donatePaymentStep",
      };
    case PROCEED_DONATE_GIFT_CONFIRMATION:
      return {
        ...state,
        currentStep: "donateConfirmationStep",
        spreedlyErrors: "",
      };
    case PROCEED_THANKYOU:
      return {
        ...state,
        ...pick(initialFormValues, INITIAL_STATE),
        currentStep: "ThankyouStep",
      };
    case SET_GIFT_CREATE_REGISTRATION_VALUES:
      return {
        ...state,
        currentFormObjectRegistration: action.payload.values,
      };
    case SET_GIFT_CREATE_IMAGE_PREVIEW:
      return {
        ...state,
        imagePreview: action.payload,
      };
    case SET_GIFT_CREATE_PAYMENT:
      return {
        ...state,
        currentFormObjectPayment: action.payload.values,
      };
    case SET_CUSTOM_FIELDS:
      return {
        ...state,
        campaignCustomFields: action.payload.campaignCustomFields,
      };
    case SET_SPREEDLY_KEY:
      return {
        ...state,
        spreedlyKey: action.payload.spreedlyKey,
      };
    case SET_SPREEDLY_TOKEN:
      return {
        ...state,
        spreedlyToken: action.payload.spreedlyToken,
      };
    case SET_SECTION_ID:
      return {
        ...state,
        sectionId: action.payload.sectionId,
      };
    case SUBMIT_MATCHING_GIFT_SUCCESS:
      return {
        ...state,
        loading: false,
      };
    case SUBMIT_MATCHING_GIFT_ERROR:
      return {
        ...state,
        loading: false,
      };
    case SET_SPREEDLY_ERRORS:
      return {
        ...state,
        spreedlyErrors: action.payload.errors,
      };
    case RESET_GIFT_CREATE_REGISTRATION:
      return {
        ...state,
        currentFormObjectRegistration: {},
        imagePreview: "",
      };
    default:
      return state;
  }
}
